import React, { useEffect, useState } from 'react';
import { financeiroAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { formatCurrency } from '@/lib/utils';
import { 
  TrendingUp, TrendingDown, DollarSign, Filter, Download,
  Building2, BookOpen, Briefcase, ChevronDown, ChevronRight,
  FileSpreadsheet, FileText, Calendar, BarChart3, ArrowUpRight, ArrowDownRight, Minus
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line, Area, AreaChart, ComposedChart } from 'recharts';

export default function DREPage() {
  const [dre, setDre] = useState(null);
  const [dreComparativo, setDreComparativo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [unidades, setUnidades] = useState([]);
  const [segmentos, setSegmentos] = useState([]);
  const [centrosCusto, setCentrosCusto] = useState([]);
  const [planoContas, setPlanoContas] = useState([]);
  
  // Tab atual
  const [activeTab, setActiveTab] = useState('mensal');
  
  // Filtros DRE Mensal
  const [filterCompetencia, setFilterCompetencia] = useState(() => {
    const now = new Date();
    return `${String(now.getMonth() + 1).padStart(2, '0')}/${now.getFullYear()}`;
  });
  const [filterUnidade, setFilterUnidade] = useState('');
  const [filterSegmento, setFilterSegmento] = useState('');
  const [filterCC, setFilterCC] = useState('');
  const [incluirRateio, setIncluirRateio] = useState(true);
  
  // Filtros DRE Comparativo
  const [filterCompInicio, setFilterCompInicio] = useState(() => {
    const now = new Date();
    now.setMonth(now.getMonth() - 5);
    return `${String(now.getMonth() + 1).padStart(2, '0')}/${now.getFullYear()}`;
  });
  const [filterCompFim, setFilterCompFim] = useState(() => {
    const now = new Date();
    return `${String(now.getMonth() + 1).padStart(2, '0')}/${now.getFullYear()}`;
  });
  const [filterUnidadeComp, setFilterUnidadeComp] = useState('');
  const [filterSegmentoComp, setFilterSegmentoComp] = useState('');
  const [filterCCComp, setFilterCCComp] = useState('');
  const [incluirRateioComp, setIncluirRateioComp] = useState(true);
  
  // Expandir detalhes
  const [expandReceitas, setExpandReceitas] = useState(false);
  const [expandDespesas, setExpandDespesas] = useState(false);

  useEffect(() => {
    loadCadastros();
  }, []);

  const loadCadastros = async () => {
    try {
      const [unidadesRes, segmentosRes, ccRes, contasRes] = await Promise.all([
        financeiroAPI.listUnidades(),
        financeiroAPI.listSegmentos(),
        financeiroAPI.listCentrosCusto(),
        financeiroAPI.listPlanoContas()
      ]);
      setUnidades(unidadesRes.data);
      setSegmentos(segmentosRes.data);
      setCentrosCusto(ccRes.data);
      setPlanoContas(contasRes.data);
    } catch (error) {
      toast.error('Erro ao carregar cadastros');
    }
  };

  const loadDRE = async () => {
    if (!filterCompetencia) {
      toast.error('Informe a competência');
      return;
    }
    
    try {
      setLoading(true);
      const res = await financeiroAPI.getDRE({
        competencia: filterCompetencia,
        cod_unidade: filterUnidade || undefined,
        cod_seg: filterSegmento || undefined,
        cod_cc: filterCC || undefined,
        incluir_rateio: incluirRateio
      });
      setDre(res.data);
    } catch (error) {
      toast.error('Erro ao carregar DRE');
    } finally {
      setLoading(false);
    }
  };

  const loadDREComparativo = async () => {
    if (!filterCompInicio || !filterCompFim) {
      toast.error('Informe o período de início e fim');
      return;
    }
    
    try {
      setLoading(true);
      const res = await financeiroAPI.getDREComparativo({
        competencia_inicio: filterCompInicio,
        competencia_fim: filterCompFim,
        cod_unidade: filterUnidadeComp || undefined,
        cod_seg: filterSegmentoComp || undefined,
        cod_cc: filterCCComp || undefined,
        incluir_rateio: incluirRateioComp
      });
      setDreComparativo(res.data);
    } catch (error) {
      toast.error('Erro ao carregar DRE Comparativo');
    } finally {
      setLoading(false);
    }
  };

  const getContaDescricao = (codConta) => {
    const conta = planoContas.find(c => c.cod_conta === codConta);
    return conta?.descricao || codConta;
  };

  const getUnidadeNome = (cod) => unidades.find(u => u.cod_unidade === cod)?.nome_unidade || cod;
  const getSegmentoNome = (cod) => segmentos.find(s => s.cod_seg === cod)?.nome_seg || cod;
  const getCCNome = (cod) => centrosCusto.find(c => c.cod_cc === cod)?.nome_cc || cod;

  const getExportParams = () => {
    const params = { competencia: filterCompetencia };
    if (filterUnidade) params.cod_unidade = filterUnidade;
    if (filterSegmento) params.cod_seg = filterSegmento;
    if (filterCC) params.cod_cc = filterCC;
    params.incluir_rateio = incluirRateio;
    return params;
  };

  const handleExportExcel = () => {
    const url = financeiroAPI.exportDREExcel(getExportParams());
    window.open(url, '_blank');
    toast.success('Download do Excel iniciado');
  };

  const handleExportPDF = () => {
    const url = financeiroAPI.exportDREPDF(getExportParams());
    window.open(url, '_blank');
    toast.success('Download do PDF iniciado');
  };

  const formatVariacao = (valor) => {
    if (valor > 0) return <span className="text-green-600 flex items-center gap-1"><ArrowUpRight className="w-4 h-4" />{valor.toFixed(1)}%</span>;
    if (valor < 0) return <span className="text-red-600 flex items-center gap-1"><ArrowDownRight className="w-4 h-4" />{Math.abs(valor).toFixed(1)}%</span>;
    return <span className="text-gray-500 flex items-center gap-1"><Minus className="w-4 h-4" />0%</span>;
  };

  const COLORS = ['#7B1FA2', '#1976D2', '#388E3C', '#F57C00', '#E91E63', '#00BCD4'];

  return (
    <div className="space-y-6" data-testid="dre-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">DRE - Demonstração de Resultado</h1>
          <p className="text-muted-foreground">Análise de receitas, deduções e despesas por período</p>
        </div>
        {dre && activeTab === 'mensal' && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleExportExcel} data-testid="export-excel-btn">
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Excel
            </Button>
            <Button variant="outline" onClick={handleExportPDF} data-testid="export-pdf-btn">
              <FileText className="w-4 h-4 mr-2" />
              PDF
            </Button>
          </div>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="mensal" data-testid="tab-mensal">
            <Calendar className="w-4 h-4 mr-2" />
            DRE Mensal
          </TabsTrigger>
          <TabsTrigger value="comparativo" data-testid="tab-comparativo">
            <BarChart3 className="w-4 h-4 mr-2" />
            Comparativo
          </TabsTrigger>
        </TabsList>

        {/* TAB DRE MENSAL */}
        <TabsContent value="mensal" className="space-y-6">
          {/* Filtros */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Filter className="w-4 h-4" />
                Filtros
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-6">
                <div className="space-y-2">
                  <Label>Competência *</Label>
                  <Input
                    value={filterCompetencia}
                    onChange={(e) => setFilterCompetencia(e.target.value)}
                    placeholder="MM/YYYY"
                    data-testid="filter-competencia"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Unidade</Label>
                  <Select value={filterUnidade || "all"} onValueChange={(v) => setFilterUnidade(v === "all" ? "" : v)}>
                    <SelectTrigger data-testid="filter-unidade">
                      <SelectValue placeholder="Todas" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas</SelectItem>
                      {unidades.map(u => (
                        <SelectItem key={u.cod_unidade} value={u.cod_unidade}>{u.nome_unidade}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Segmento</Label>
                  <Select value={filterSegmento || "all"} onValueChange={(v) => setFilterSegmento(v === "all" ? "" : v)}>
                    <SelectTrigger data-testid="filter-segmento">
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {segmentos.filter(s => s.cod_seg !== 'GERAL').map(s => (
                        <SelectItem key={s.cod_seg} value={s.cod_seg}>{s.nome_seg}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Centro de Custo</Label>
                  <Select value={filterCC || "all"} onValueChange={(v) => setFilterCC(v === "all" ? "" : v)}>
                    <SelectTrigger data-testid="filter-cc">
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {centrosCusto.map(c => (
                        <SelectItem key={c.cod_cc} value={c.cod_cc}>{c.nome_cc}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Incluir Rateio</Label>
                  <Select value={incluirRateio ? "sim" : "nao"} onValueChange={(v) => setIncluirRateio(v === "sim")}>
                    <SelectTrigger data-testid="filter-rateio">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sim">Sim</SelectItem>
                      <SelectItem value="nao">Não</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2 flex items-end">
                  <Button onClick={loadDRE} disabled={loading} className="w-full" data-testid="btn-gerar-dre">
                    {loading ? 'Carregando...' : 'Gerar DRE'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {dre && (
            <>
              {/* Resumo Principal */}
              <div className="grid gap-4 md:grid-cols-5">
                <Card className="border-green-200 bg-green-50/50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-green-700">
                      <TrendingUp className="w-4 h-4 inline mr-2" />
                      Receita Bruta
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-green-700" data-testid="receita-bruta">
                      {formatCurrency(dre.resumo.receita_bruta)}
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-amber-200 bg-amber-50/50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-amber-700">
                      <TrendingDown className="w-4 h-4 inline mr-2" />
                      Deduções
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-amber-700" data-testid="deducoes">
                      -{formatCurrency(dre.resumo.deducoes)}
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-blue-200 bg-blue-50/50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-blue-700">
                      <DollarSign className="w-4 h-4 inline mr-2" />
                      Receita Líquida
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-blue-700" data-testid="receita-liquida">
                      {formatCurrency(dre.resumo.receita_liquida)}
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-red-200 bg-red-50/50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-red-700">
                      <TrendingDown className="w-4 h-4 inline mr-2" />
                      Despesas
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-red-700" data-testid="despesas">
                      -{formatCurrency(dre.resumo.despesas)}
                    </div>
                  </CardContent>
                </Card>

                <Card className={`border-2 ${dre.resumo.resultado_operacional >= 0 ? 'border-green-500 bg-green-100' : 'border-red-500 bg-red-100'}`}>
                  <CardHeader className="pb-2">
                    <CardTitle className={`text-sm font-medium ${dre.resumo.resultado_operacional >= 0 ? 'text-green-800' : 'text-red-800'}`}>
                      Resultado Operacional
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className={`text-2xl font-bold ${dre.resumo.resultado_operacional >= 0 ? 'text-green-800' : 'text-red-800'}`} data-testid="resultado">
                      {formatCurrency(dre.resumo.resultado_operacional)}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {dre.resumo.resultado_operacional >= 0 ? 'Superávit' : 'Déficit'}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* DRE Detalhado */}
              <Card>
                <CardHeader>
                  <CardTitle>Demonstração de Resultado Detalhada</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableBody>
                      {/* RECEITAS */}
                      <TableRow 
                        className="bg-green-50 cursor-pointer hover:bg-green-100"
                        onClick={() => setExpandReceitas(!expandReceitas)}
                      >
                        <TableCell className="font-bold">
                          {expandReceitas ? <ChevronDown className="w-4 h-4 inline mr-2" /> : <ChevronRight className="w-4 h-4 inline mr-2" />}
                          RECEITA BRUTA
                        </TableCell>
                        <TableCell className="text-right font-bold text-green-700">
                          {formatCurrency(dre.resumo.receita_bruta)}
                        </TableCell>
                      </TableRow>
                      
                      {expandReceitas && Object.entries(dre.receitas_por_conta || {}).map(([conta, valor]) => (
                        <TableRow key={conta} className="text-sm">
                          <TableCell className="pl-8">
                            <span className="font-mono text-xs text-muted-foreground mr-2">{conta}</span>
                            {getContaDescricao(conta)}
                          </TableCell>
                          <TableCell className="text-right text-green-600">
                            {formatCurrency(valor)}
                          </TableCell>
                        </TableRow>
                      ))}

                      {/* DEDUÇÕES */}
                      <TableRow className="bg-amber-50">
                        <TableCell className="font-bold text-amber-700">
                          (-) DEDUÇÕES
                        </TableCell>
                        <TableCell className="text-right font-bold text-amber-700">
                          -{formatCurrency(dre.resumo.deducoes)}
                        </TableCell>
                      </TableRow>

                      {/* RECEITA LÍQUIDA */}
                      <TableRow className="bg-blue-100 border-t-2 border-blue-300">
                        <TableCell className="font-bold text-blue-800">
                          (=) RECEITA LÍQUIDA
                        </TableCell>
                        <TableCell className="text-right font-bold text-blue-800">
                          {formatCurrency(dre.resumo.receita_liquida)}
                        </TableCell>
                      </TableRow>

                      {/* DESPESAS */}
                      <TableRow 
                        className="bg-red-50 cursor-pointer hover:bg-red-100"
                        onClick={() => setExpandDespesas(!expandDespesas)}
                      >
                        <TableCell className="font-bold">
                          {expandDespesas ? <ChevronDown className="w-4 h-4 inline mr-2" /> : <ChevronRight className="w-4 h-4 inline mr-2" />}
                          (-) DESPESAS OPERACIONAIS
                        </TableCell>
                        <TableCell className="text-right font-bold text-red-700">
                          -{formatCurrency(dre.resumo.despesas)}
                        </TableCell>
                      </TableRow>

                      {expandDespesas && Object.entries(dre.despesas_por_conta || {}).sort((a, b) => a[0].localeCompare(b[0])).map(([conta, valor]) => (
                        <TableRow key={conta} className="text-sm">
                          <TableCell className="pl-8">
                            <span className="font-mono text-xs text-muted-foreground mr-2">{conta}</span>
                            {getContaDescricao(conta)}
                          </TableCell>
                          <TableCell className="text-right text-red-600">
                            -{formatCurrency(valor)}
                          </TableCell>
                        </TableRow>
                      ))}

                      {/* RESULTADO */}
                      <TableRow className={`border-t-4 ${dre.resumo.resultado_operacional >= 0 ? 'bg-green-200 border-green-400' : 'bg-red-200 border-red-400'}`}>
                        <TableCell className={`font-bold text-lg ${dre.resumo.resultado_operacional >= 0 ? 'text-green-900' : 'text-red-900'}`}>
                          (=) RESULTADO OPERACIONAL
                        </TableCell>
                        <TableCell className={`text-right font-bold text-lg ${dre.resumo.resultado_operacional >= 0 ? 'text-green-900' : 'text-red-900'}`}>
                          {formatCurrency(dre.resumo.resultado_operacional)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Gráficos de Análise */}
              <div className="grid gap-4 md:grid-cols-2">
                {/* Gráfico Pizza - Despesas por Centro de Custo */}
                {Object.keys(dre.despesas_por_cc || {}).length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Briefcase className="w-5 h-5" />
                        Despesas por Centro de Custo
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-[300px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={Object.entries(dre.despesas_por_cc).map(([cc, valor]) => ({
                                name: centrosCusto.find(c => c.cod_cc === cc)?.nome_cc || cc,
                                value: valor,
                                percentage: ((valor / dre.resumo.despesas) * 100).toFixed(1)
                              }))}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              label={({ name, percentage }) => `${name}: ${percentage}%`}
                              outerRadius={100}
                              fill="#8884d8"
                              dataKey="value"
                            >
                              {Object.entries(dre.despesas_por_cc).map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value) => formatCurrency(value)} />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Gráfico Pizza - Despesas por Segmento */}
                {Object.keys(dre.despesas_por_seg || {}).length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <BookOpen className="w-5 h-5" />
                        Despesas por Segmento
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-[300px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={Object.entries(dre.despesas_por_seg).map(([seg, valor]) => ({
                                name: segmentos.find(s => s.cod_seg === seg)?.nome_seg || seg,
                                value: valor,
                                percentage: ((valor / dre.resumo.despesas) * 100).toFixed(1)
                              }))}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              label={({ percentage }) => `${percentage}%`}
                              outerRadius={100}
                              fill="#8884d8"
                              dataKey="value"
                            >
                              {Object.entries(dre.despesas_por_seg).map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value) => formatCurrency(value)} />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Gráfico de Barras - Composição do Resultado */}
              <Card>
                <CardHeader>
                  <CardTitle>Composição do Resultado</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[250px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={[
                          { name: 'Receita Bruta', valor: dre.resumo.receita_bruta, fill: '#4CAF50' },
                          { name: 'Deduções', valor: -dre.resumo.deducoes, fill: '#FF9800' },
                          { name: 'Rec. Líquida', valor: dre.resumo.receita_liquida, fill: '#2196F3' },
                          { name: 'Despesas', valor: -dre.resumo.despesas, fill: '#f44336' },
                          { name: 'Resultado', valor: dre.resumo.resultado_operacional, fill: dre.resumo.resultado_operacional >= 0 ? '#4CAF50' : '#f44336' }
                        ]}
                        layout="vertical"
                        margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" tickFormatter={(value) => `R$ ${(value/1000).toFixed(0)}k`} />
                        <YAxis type="category" dataKey="name" width={90} />
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Bar dataKey="valor" fill="#8884d8">
                          {[
                            { fill: '#4CAF50' },
                            { fill: '#FF9800' },
                            { fill: '#2196F3' },
                            { fill: '#f44336' },
                            { fill: dre.resumo.resultado_operacional >= 0 ? '#4CAF50' : '#f44336' }
                          ].map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Info */}
              <div className="text-sm text-muted-foreground text-center">
                Dados gerados a partir de {dre.qtd_lancamentos} lançamento(s) e {dre.qtd_rateios} item(ns) de rateio
              </div>
            </>
          )}
        </TabsContent>

        {/* TAB DRE COMPARATIVO */}
        <TabsContent value="comparativo" className="space-y-6">
          {/* Filtros Comparativo */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Filter className="w-4 h-4" />
                Filtros do Período
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-7">
                <div className="space-y-2">
                  <Label>Competência Início *</Label>
                  <Input
                    value={filterCompInicio}
                    onChange={(e) => setFilterCompInicio(e.target.value)}
                    placeholder="MM/YYYY"
                    data-testid="filter-comp-inicio"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Competência Fim *</Label>
                  <Input
                    value={filterCompFim}
                    onChange={(e) => setFilterCompFim(e.target.value)}
                    placeholder="MM/YYYY"
                    data-testid="filter-comp-fim"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Unidade</Label>
                  <Select value={filterUnidadeComp || "all"} onValueChange={(v) => setFilterUnidadeComp(v === "all" ? "" : v)}>
                    <SelectTrigger data-testid="filter-unidade-comp">
                      <SelectValue placeholder="Todas" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas</SelectItem>
                      {unidades.map(u => (
                        <SelectItem key={u.cod_unidade} value={u.cod_unidade}>{u.nome_unidade}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Segmento</Label>
                  <Select value={filterSegmentoComp || "all"} onValueChange={(v) => setFilterSegmentoComp(v === "all" ? "" : v)}>
                    <SelectTrigger data-testid="filter-segmento-comp">
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {segmentos.filter(s => s.cod_seg !== 'GERAL').map(s => (
                        <SelectItem key={s.cod_seg} value={s.cod_seg}>{s.nome_seg}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Centro de Custo</Label>
                  <Select value={filterCCComp || "all"} onValueChange={(v) => setFilterCCComp(v === "all" ? "" : v)}>
                    <SelectTrigger data-testid="filter-cc-comp">
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {centrosCusto.map(c => (
                        <SelectItem key={c.cod_cc} value={c.cod_cc}>{c.nome_cc}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Incluir Rateio</Label>
                  <Select value={incluirRateioComp ? "sim" : "nao"} onValueChange={(v) => setIncluirRateioComp(v === "sim")}>
                    <SelectTrigger data-testid="filter-rateio-comp">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sim">Sim</SelectItem>
                      <SelectItem value="nao">Não</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2 flex items-end">
                  <Button onClick={loadDREComparativo} disabled={loading} className="w-full" data-testid="btn-gerar-comparativo">
                    {loading ? 'Carregando...' : 'Gerar Comparativo'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {dreComparativo && (
            <>
              {/* Resumo do Período */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    Resumo do Período: {dreComparativo.periodo.inicio} a {dreComparativo.periodo.fim} ({dreComparativo.periodo.num_meses} meses)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 md:grid-cols-4">
                    <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                      <p className="text-sm text-green-700 font-medium">Total Receita Bruta</p>
                      <p className="text-2xl font-bold text-green-800">{formatCurrency(dreComparativo.totais.receita_bruta)}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Média: {formatCurrency(dreComparativo.medias.receita_bruta)}/mês
                      </p>
                    </div>
                    <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                      <p className="text-sm text-red-700 font-medium">Total Despesas</p>
                      <p className="text-2xl font-bold text-red-800">{formatCurrency(dreComparativo.totais.despesas)}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Média: {formatCurrency(dreComparativo.medias.despesas)}/mês
                      </p>
                    </div>
                    <div className={`p-4 rounded-lg border ${dreComparativo.totais.resultado >= 0 ? 'bg-green-100 border-green-300' : 'bg-red-100 border-red-300'}`}>
                      <p className={`text-sm font-medium ${dreComparativo.totais.resultado >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                        Total Resultado
                      </p>
                      <p className={`text-2xl font-bold ${dreComparativo.totais.resultado >= 0 ? 'text-green-800' : 'text-red-800'}`}>
                        {formatCurrency(dreComparativo.totais.resultado)}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Média: {formatCurrency(dreComparativo.medias.resultado)}/mês
                      </p>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-sm text-blue-700 font-medium">Variação no Período</p>
                      <div className="space-y-1 mt-2">
                        <div className="flex justify-between items-center text-sm">
                          <span>Receita:</span>
                          {formatVariacao(dreComparativo.variacao_percentual.receita_bruta)}
                        </div>
                        <div className="flex justify-between items-center text-sm">
                          <span>Despesas:</span>
                          {formatVariacao(dreComparativo.variacao_percentual.despesas)}
                        </div>
                        <div className="flex justify-between items-center text-sm">
                          <span>Resultado:</span>
                          {formatVariacao(dreComparativo.variacao_percentual.resultado)}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Gráfico de Evolução */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" />
                    Evolução Mensal
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={dreComparativo.dados_mensais}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="competencia" />
                        <YAxis tickFormatter={(value) => `R$ ${(value/1000).toFixed(0)}k`} />
                        <Tooltip 
                          formatter={(value) => formatCurrency(value)} 
                          labelFormatter={(label) => `Competência: ${label}`}
                        />
                        <Legend />
                        <Bar dataKey="receita_bruta" name="Receita Bruta" fill="#4CAF50" />
                        <Bar dataKey="despesas" name="Despesas" fill="#f44336" />
                        <Line type="monotone" dataKey="resultado" name="Resultado" stroke="#2196F3" strokeWidth={3} dot={{ r: 5 }} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Gráfico de Área - Receita vs Despesa */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5" />
                    Receita Líquida vs Despesas
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={dreComparativo.dados_mensais}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="competencia" />
                        <YAxis tickFormatter={(value) => `R$ ${(value/1000).toFixed(0)}k`} />
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Legend />
                        <Area type="monotone" dataKey="receita_liquida" name="Receita Líquida" stackId="1" stroke="#4CAF50" fill="#4CAF50" fillOpacity={0.6} />
                        <Area type="monotone" dataKey="despesas" name="Despesas" stackId="2" stroke="#f44336" fill="#f44336" fillOpacity={0.6} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Tabela Comparativa Detalhada */}
              <Card>
                <CardHeader>
                  <CardTitle>Detalhamento Mensal</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="font-bold">Competência</TableHead>
                          <TableHead className="text-right font-bold text-green-700">Receita Bruta</TableHead>
                          <TableHead className="text-right font-bold text-amber-700">Deduções</TableHead>
                          <TableHead className="text-right font-bold text-blue-700">Receita Líquida</TableHead>
                          <TableHead className="text-right font-bold text-red-700">Despesas</TableHead>
                          <TableHead className="text-right font-bold">Resultado</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {dreComparativo.dados_mensais.map((mes, index) => (
                          <TableRow key={mes.competencia} className={index % 2 === 0 ? 'bg-muted/30' : ''}>
                            <TableCell className="font-medium">{mes.competencia}</TableCell>
                            <TableCell className="text-right text-green-600">{formatCurrency(mes.receita_bruta)}</TableCell>
                            <TableCell className="text-right text-amber-600">-{formatCurrency(mes.deducoes)}</TableCell>
                            <TableCell className="text-right text-blue-600">{formatCurrency(mes.receita_liquida)}</TableCell>
                            <TableCell className="text-right text-red-600">-{formatCurrency(mes.despesas)}</TableCell>
                            <TableCell className={`text-right font-bold ${mes.resultado >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                              {formatCurrency(mes.resultado)}
                            </TableCell>
                          </TableRow>
                        ))}
                        {/* Linha de Totais */}
                        <TableRow className="bg-gray-100 border-t-2 font-bold">
                          <TableCell className="font-bold">TOTAL</TableCell>
                          <TableCell className="text-right text-green-700">{formatCurrency(dreComparativo.totais.receita_bruta)}</TableCell>
                          <TableCell className="text-right text-amber-700">-{formatCurrency(dreComparativo.totais.deducoes)}</TableCell>
                          <TableCell className="text-right text-blue-700">{formatCurrency(dreComparativo.totais.receita_liquida)}</TableCell>
                          <TableCell className="text-right text-red-700">-{formatCurrency(dreComparativo.totais.despesas)}</TableCell>
                          <TableCell className={`text-right font-bold ${dreComparativo.totais.resultado >= 0 ? 'text-green-800' : 'text-red-800'}`}>
                            {formatCurrency(dreComparativo.totais.resultado)}
                          </TableCell>
                        </TableRow>
                        {/* Linha de Médias */}
                        <TableRow className="bg-blue-50">
                          <TableCell className="font-medium text-blue-700">MÉDIA MENSAL</TableCell>
                          <TableCell className="text-right text-blue-600">{formatCurrency(dreComparativo.medias.receita_bruta)}</TableCell>
                          <TableCell className="text-right text-blue-600">-</TableCell>
                          <TableCell className="text-right text-blue-600">-</TableCell>
                          <TableCell className="text-right text-blue-600">{formatCurrency(dreComparativo.medias.despesas)}</TableCell>
                          <TableCell className="text-right text-blue-700 font-bold">{formatCurrency(dreComparativo.medias.resultado)}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>

              {/* Análise por Centro de Custo ao longo do período */}
              {dreComparativo.dados_mensais.some(m => Object.keys(m.despesas_por_cc || {}).length > 0) && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Briefcase className="w-5 h-5" />
                      Evolução de Despesas por Centro de Custo
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[350px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={dreComparativo.dados_mensais}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="competencia" />
                          <YAxis tickFormatter={(value) => `R$ ${(value/1000).toFixed(0)}k`} />
                          <Tooltip formatter={(value) => formatCurrency(value)} />
                          <Legend />
                          {/* Pegando todos os CCs únicos */}
                          {(() => {
                            const allCCs = new Set();
                            dreComparativo.dados_mensais.forEach(m => {
                              Object.keys(m.despesas_por_cc || {}).forEach(cc => allCCs.add(cc));
                            });
                            return Array.from(allCCs).map((cc, index) => (
                              <Bar 
                                key={cc} 
                                dataKey={`despesas_por_cc.${cc}`} 
                                name={getCCNome(cc)} 
                                stackId="a"
                                fill={COLORS[index % COLORS.length]} 
                              />
                            ));
                          })()}
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
